import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, Bell, BellRing, Mail, CalendarPlus, Copy, RefreshCw, Clock3, Tag, X, AlertTriangle, ChevronDown, ChevronRight, Phone, Pencil, BadgeCheck, Check, UserSearch, ShieldCheck, Landmark, Star, Send, Download, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import OabSolicitacoes from "@/components/OabSolicitacoes";
import AssinarAgendaGuia from "@/components/AssinarAgendaGuia";
import Recolhivel from "@/components/Recolhivel";
import IdentidadeCard from "@/components/IdentidadeCard";
import { subscribeToPush, unsubscribeFromPush, isPushSupported, isSubscribedToPush, getPushPermission } from "@/lib/push";

import { Dica } from "@/components/Dica";
import { SEL_CINZA } from "@/lib/selecao";
// Lote 68 — siglas dos tribunais que o usuário pode marcar para a leitura do caderno diário do DJEN.
const _TJ_UFS = ["AC","AL","AP","AM","BA","CE","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];
const CADERNO_GRUPOS = [
  { titulo: "Justiça do Trabalho (TRT)", itens: Array.from({ length: 24 }, (_, i) => `TRT${i + 1}`) },
  { titulo: "Justiça Estadual (TJ)", itens: [..._TJ_UFS.map((u) => `TJ${u}`), "TJDFT"] },
  { titulo: "Justiça Federal (TRF)", itens: Array.from({ length: 6 }, (_, i) => `TRF${i + 1}`) },
  { titulo: "Superiores", itens: ["TST", "STJ"] },
];

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

// Descrição resumida por padrão, com "Saiba mais" pra quem quiser o detalhe completo —
// evita que a tela de Configurações fique poluída de parágrafo em toda seção.
const SectionIntro = ({ summary, more }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-6 max-w-2xl">
      <p className="text-sm text-stone-500">{summary}</p>
      {open && <p className="text-sm text-stone-500 mt-2 leading-relaxed">{more}</p>}
      <button
        type="button"
        data-testid="section-saiba-mais"
        onClick={() => setOpen((o) => !o)}
        className="text-xs font-semibold text-emerald-brand hover:underline mt-1.5 flex items-center gap-1"
      >
        {open ? "Ver menos" : "Saiba mais"}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2.5} />
      </button>
    </div>
  );
};

// Lote 132 (Robson, 27/10/2026) — "Configurações ficaram com muitas funções: aba expansível para cada uma".
// Cada seção é uma linha recolhida (título + resumo do estado atual) que abre com 1 toque; as seções são
// independentes (abrir uma não fecha as outras). O conteúdo continua montado (só escondido) para não perder
// o que foi digitado ao recolher. O botão Salvar fica dentro de cada seção, como antes.
const SecaoConfig = ({ id, testid, titulo, icon: Icon, resumo, abrirInicial = false, children }) => {
  const [open, setOpen] = useState(!!abrirInicial);
  return (
    <section className="bg-white border border-stone-200 rounded-md" data-testid={testid} data-secao={id} data-aberta={open ? "1" : "0"}>
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 min-h-[56px] px-5 sm:px-8 py-3 text-left hover:bg-stone-50 rounded-md"
      >
        {Icon ? <Icon className="w-4 h-4 text-emerald-brand shrink-0" strokeWidth={2} /> : null}
        <span className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
          <h2 className="font-display text-base sm:text-lg font-semibold text-stone-900 leading-snug sm:shrink-0">{titulo}</h2>
          {resumo ? <span className="text-xs text-stone-500 truncate min-w-0" data-testid={`${testid}-resumo`}>{resumo}</span> : null}
        </span>
        <span className="pl-2 text-stone-400 shrink-0">
          {open ? <ChevronDown className="w-4 h-4" strokeWidth={2} /> : <ChevronRight className="w-4 h-4" strokeWidth={2} />}
        </span>
      </button>
      <div hidden={!open} className="px-5 sm:px-8 pt-1 pb-8">{children}</div>
    </section>
  );
};

export default function SettingsPage() {
  const { user } = useAuth();
  // Lote 87 — só o administrador (is_owner) inclui/remove OAB direto; os demais por pedido.
  const oabTravada = !!user && !user.is_owner;
  const [removerOab, setRemoverOab] = useState(null);
  const [oabs, setOabs] = useState([]);
  const [numero, setNumero] = useState("");
  const [uf, setUf] = useState("SP");
  // Lote 59 — busca complementar OPCIONAL por nome do advogado (pedido do Robson:
  // "dá pra incluir a busca por nome como opcional?"). nomeAdvogado é o campo do
  // formulário de cadastro; editingNomeOabId/editingNomeValue controlam a edição
  // inline desse campo nas OABs já cadastradas (não dá pra simplesmente re-adicionar,
  // perderia o histórico de diagnóstico de sincronização).
  const [nomeAdvogado, setNomeAdvogado] = useState("");
  const [editingNomeOabId, setEditingNomeOabId] = useState(null);
  const [editingNomeValue, setEditingNomeValue] = useState("");
  const [savingNome, setSavingNome] = useState(false);
  const [settings, setSettings] = useState({
    days_before: 3, post_hearing_days: 5, channels: ["email", "whatsapp"],
    auto_prazo_audiencias: true, auto_prazo_publicacoes: false, keywords: [],
    push_enabled: true, push_day_before_enabled: true, push_day_before_hours: 24,
    push_hour_before_enabled: true, push_hour_before_hours: 1, push_conflict_alerts_enabled: true,
    email_self_enabled: true, email_self_days_before: 1,
    digest_manha_enabled: true, digest_tarde_enabled: true, digest_sempre: false, ia_assistente_enabled: true,
    ics_inc_link: true, ics_inc_sala: true, ics_inc_partes: true, ics_inc_local: true, ics_inc_fase: true, ics_inc_descricao: true, ics_inc_endereco: true, ics_inc_cliente: false, ics_inc_abrir: true,
    call_enabled: false, call_phone: "", call_day_before_enabled: true, call_day_before_days: 1,
    call_minutes_before_enabled: true, call_minutes_before: 60, call_morning_enabled: true, call_morning_hour: 7,
    call_event_types: ["audiencia", "prazo", "reuniao"],
    ai_mask_names: true,
    caderno_tribunais: [],
    caderno_auto_tribunais: true,
    prio_push_enabled: true, prio_email_enabled: true, prio_revisao_padrao: "quinzenal",
  });
  const [keywordInput, setKeywordInput] = useState("");
  const [icsFeedUrl, setIcsFeedUrl] = useState(null);
  const [rotatingFeed, setRotatingFeed] = useState(false);
  // Lote 98: guia "Ver minha agenda em outro calendário" — abre pelo botão ou por /config?guia=assinatura (dica do letreiro).
  const [searchParams, setSearchParams] = useSearchParams();
  const [guiaOpen, setGuiaOpen] = useState(searchParams.get("guia") === "assinatura");
  const fecharGuia = (v) => {
    setGuiaOpen(v);
    if (!v && searchParams.get("guia")) { searchParams.delete("guia"); setSearchParams(searchParams, { replace: true }); }
  };
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const load = async () => {
    const [o, s] = await Promise.all([http.get("/oab"), http.get("/settings")]);
    setOabs(o.data);
    setSettings({
      days_before: s.data.days_before ?? 3,
      post_hearing_days: s.data.post_hearing_days ?? 5,
      // Fallback defensivo: um registro antigo de settings sem o campo "channels"
      // travava a tela em branco (checkbox chamando .includes em undefined) — achado
      // ao investigar o relato do Robson de "Configurações não abre" (17/09/2026).
      channels: s.data.channels || ["email", "whatsapp"],
      auto_prazo_audiencias: s.data.auto_prazo_audiencias ?? true,
      auto_prazo_publicacoes: s.data.auto_prazo_publicacoes ?? false,
      keywords: s.data.keywords || [],
      push_enabled: s.data.push_enabled ?? true,
      push_day_before_enabled: s.data.push_day_before_enabled ?? true,
      push_day_before_hours: s.data.push_day_before_hours ?? 24,
      push_hour_before_enabled: s.data.push_hour_before_enabled ?? true,
      push_hour_before_hours: s.data.push_hour_before_hours ?? 1,
      push_conflict_alerts_enabled: s.data.push_conflict_alerts_enabled ?? true,
      email_self_enabled: s.data.email_self_enabled ?? true,
      email_self_days_before: s.data.email_self_days_before ?? 1,
      ia_assistente_enabled: s.data.ia_assistente_enabled ?? true,
      digest_manha_enabled: s.data.digest_manha_enabled ?? true,
      digest_tarde_enabled: s.data.digest_tarde_enabled ?? true,
      digest_sempre: s.data.digest_sempre ?? false,
      ics_inc_link: s.data.ics_inc_link ?? true,
      ics_inc_sala: s.data.ics_inc_sala ?? true,
      ics_inc_partes: s.data.ics_inc_partes ?? true,
      ics_inc_local: s.data.ics_inc_local ?? true,
      ics_inc_fase: s.data.ics_inc_fase ?? true,
      ics_inc_descricao: s.data.ics_inc_descricao ?? true,
      ics_inc_endereco: s.data.ics_inc_endereco ?? true,
      ics_inc_cliente: s.data.ics_inc_cliente ?? false,
      ics_inc_abrir: s.data.ics_inc_abrir ?? true,
      call_enabled: s.data.call_enabled ?? false,
      call_phone: s.data.call_phone || "",
      call_day_before_enabled: s.data.call_day_before_enabled ?? true,
      call_day_before_days: s.data.call_day_before_days ?? 1,
      call_minutes_before_enabled: s.data.call_minutes_before_enabled ?? true,
      call_event_types: s.data.call_event_types || ["audiencia", "prazo", "reuniao"],
      call_minutes_before: s.data.call_minutes_before ?? 60,
      call_morning_enabled: s.data.call_morning_enabled ?? true,
      call_morning_hour: s.data.call_morning_hour ?? 7,
      ai_mask_names: s.data.ai_mask_names ?? true,
      caderno_tribunais: s.data.caderno_tribunais || [],
      caderno_auto_tribunais: s.data.caderno_auto_tribunais ?? true,
      prio_push_enabled: s.data.prio_push_enabled ?? true,
      prio_email_enabled: s.data.prio_email_enabled ?? true,
      prio_revisao_padrao: s.data.prio_revisao_padrao || "quinzenal",
    });
  };

  const loadIcsFeed = () =>
    http.get("/settings/ics-feed").then((r) => setIcsFeedUrl(r.data.feed_url)).catch(() => {});

  useEffect(() => {
    load();
    loadIcsFeed();
    isSubscribedToPush().then(setPushSubscribed);
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushSubscribed) {
        await unsubscribeFromPush();
        setPushSubscribed(false);
        toast.info("Notificações push desativadas neste navegador");
      } else {
        await subscribeToPush();
        setPushSubscribed(true);
        toast.success("Notificações push ativadas neste navegador");
      }
    } catch (e) {
      toast.error(e?.message || "Não foi possível ativar as notificações");
    } finally {
      setPushBusy(false);
    }
  };

  const copyIcsFeed = async () => {
    if (!icsFeedUrl) return;
    try {
      await navigator.clipboard.writeText(icsFeedUrl);
      toast.success("Link copiado");
    } catch {
      toast.error("Não deu pra copiar automaticamente — selecione e copie manualmente");
    }
  };

  // Lote 150 — "Baixar cópia (.ics)": o mesmo calendário, como arquivo, para guardar ou importar.
  const [baixandoIcs, setBaixandoIcs] = useState(false);
  const baixarCopiaIcs = async () => {
    setBaixandoIcs(true);
    try {
      const r = await http.get("/publications/export.ics", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `iapauta-agenda-${new Date().toISOString().slice(0, 10)}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Cópia da agenda baixada");
    } catch {
      toast.error("Não consegui baixar a cópia agora");
    } finally {
      setBaixandoIcs(false);
    }
  };

  const rotateIcsFeed = async () => {
    setRotatingFeed(true);
    try {
      const r = await http.post("/settings/ics-feed/rotate");
      setIcsFeedUrl(r.data.feed_url);
      toast.success("Novo link gerado — assinaturas antigas param de atualizar");
    } catch {
      toast.error("Erro ao gerar novo link");
    } finally {
      setRotatingFeed(false);
      setConfirmRotate(false);
    }
  };

  const addOab = async () => {
    if (!numero) return;
    try {
      await http.post("/oab", { numero, uf, nome_advogado: nomeAdvogado.trim() || null });
      toast.success("OAB cadastrada");
      setNumero("");
      setNomeAdvogado("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  const delOab = async (id) => {
    await http.delete(`/oab/${id}`);
    toast.info("OAB removida");
    load();
  };

  // Lote 59 — salva/edita a busca por nome numa OAB já cadastrada.
  const startEditNome = (o) => {
    setEditingNomeOabId(o.oab_id);
    setEditingNomeValue(o.nome_advogado || "");
  };
  const cancelEditNome = () => {
    setEditingNomeOabId(null);
    setEditingNomeValue("");
  };
  const saveNome = async (oabId) => {
    setSavingNome(true);
    try {
      await http.patch(`/oab/${oabId}`, { nome_advogado: editingNomeValue.trim() || null });
      toast.success("Busca por nome atualizada");
      cancelEditNome();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSavingNome(false);
    }
  };

  const saveSettings = async () => {
    try {
      await http.put("/settings", settings);
      toast.success("Preferências salvas");
    } catch { toast.error("Erro ao salvar"); }
  };

  // Lote 130 — manda um resumo de exemplo agora (não conta como o envio do dia).
  const [enviandoDigest, setEnviandoDigest] = useState(false);
  const enviarExemploDigest = async () => {
    setEnviandoDigest(true);
    try {
      const r = await http.post("/digest/teste", { tipo: settings.digest_tarde_enabled && !settings.digest_manha_enabled ? "tarde" : "manha" });
      toast.success(`Exemplo enviado para ${r.data.para}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível enviar o exemplo agora");
    } finally { setEnviandoDigest(false); }
  };

  const toggleTribunal = (sigla) => {
    setSettings((s) => {
      const cur = s.caderno_tribunais || [];
      return { ...s, caderno_tribunais: cur.includes(sigla) ? cur.filter((x) => x !== sigla) : [...cur, sigla] };
    });
  };

  const toggleChannel = (ch) => {
    setSettings((s) => {
      const channels = s.channels || [];
      return { ...s, channels: channels.includes(ch) ? channels.filter((x) => x !== ch) : [...channels, ch] };
    });
  };

  // Filtro por tipo de evento no lembrete por ligação (pedido do Robson, 29/09/2026) —
  // mesmo padrão do toggleChannel acima, mas pra call_event_types.
  const toggleCallEventType = (type) => {
    setSettings((s) => {
      const types = s.call_event_types || [];
      return {
        ...s,
        call_event_types: types.includes(type) ? types.filter((t) => t !== type) : [...types, type],
      };
    });
  };

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (!kw) return;
    setSettings((s) => {
      const current = s.keywords || [];
      if (current.some((k) => k.toLowerCase() === kw.toLowerCase())) return s;
      return { ...s, keywords: [...current, kw] };
    });
    setKeywordInput("");
  };
  const removeKeyword = (kw) => {
    setSettings((s) => ({ ...s, keywords: (s.keywords || []).filter((k) => k !== kw) }));
  };

  // Lote 132: resumo do estado de cada seção, mostrado na linha recolhida (dá para conferir sem abrir).
  const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;
  const nomesCanais = { email: "e-mail", whatsapp: "WhatsApp" };
  const resumos = {
    oab: oabs.length ? oabs.map((o) => `${o.numero}/${o.uf}`).join(", ") : "nenhuma cadastrada",
    agenda: icsFeedUrl ? "link pronto para assinar" : "",
    prazos: (() => {
      const l = [settings.auto_prazo_audiencias && "da ata", settings.auto_prazo_publicacoes && "das publicações"].filter(Boolean);
      return l.length ? `automáticos: ${l.join(" e ")}` : "automáticos desligados";
    })(),
    assistente: settings.ia_assistente_enabled ? "ligado · 40 perguntas por dia" : "desligado",
    privacidade: settings.ai_mask_names ? "nomes ocultos na IA" : "nomes visíveis na IA",
    tribunais: (settings.caderno_tribunais || []).length
      ? plural(settings.caderno_tribunais.length, "tribunal marcado", "tribunais marcados")
      : "automático pela UF da OAB",
    palavras: (settings.keywords || []).length ? plural(settings.keywords.length, "palavra", "palavras") : "nenhuma",
    identidade: user?.identidade ? `${user.identidade.nome}${user.identidade.tem_logo ? " · com logo" : ""}` : "marca do IApauta",
    lembretes: `cliente ${settings.days_before} d antes${(settings.channels || []).length ? ` · ${(settings.channels || []).map((c) => nomesCanais[c] || c).join(" + ")}` : ""}`,
    emailself: settings.email_self_enabled ? `ligado · ${plural(settings.email_self_days_before, "dia antes", "dias antes")}` : "desligado",
    digest: (() => {
      const l = [settings.digest_manha_enabled && "manhã", settings.digest_tarde_enabled && "tarde"].filter(Boolean);
      return l.length ? l.join(" e ") : "desligado";
    })(),
    prio: `${[settings.prio_push_enabled && "push", settings.prio_email_enabled && "e-mail"].filter(Boolean).join(" + ") || "sem aviso"} · revisão ${settings.prio_revisao_padrao}`,
    push: !settings.push_enabled ? "desligadas" : pushSubscribed ? "ativas neste aparelho" : "não ativadas neste aparelho",
    ligacao: settings.call_enabled ? `ligado${settings.call_phone ? ` · ${settings.call_phone}` : ""}` : "desligada",
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-4xl mx-auto space-y-3 sm:space-y-4">
      <header className="mb-4">
        <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand mb-2">Configurações</div>
        <h1 className="font-display text-3xl font-bold text-stone-900">Ajustes gerais</h1>
        <a href="/termo" target="_blank" rel="noreferrer" data-testid="settings-termo-link"
          className="text-xs text-stone-500 underline hover:text-stone-800 mt-2 inline-block">
          Termo de Uso e Aviso de Privacidade
        </a>
      </header>

      <SecaoConfig id="oab" testid="cfg-oab" titulo="OABs monitoradas" resumo={resumos.oab}>
        <SectionIntro
          summary="Consultamos o DJEN diariamente para cada OAB cadastrada."
          more="A busca roda automaticamente duas vezes ao dia (6h e 16h) e detecta publicações novas nos processos vinculados a essas OABs, sem precisar clicar em nada. Nesta fase de testes, o limite é de 2 OABs por conta."
        />

        {oabTravada ? (
          <OabSolicitacoes oabs={oabs} remover={removerOab} onClearRemover={() => setRemoverOab(null)} />
        ) : (
          <>
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs text-stone-500 mb-1.5 block">Número da OAB</Label>
            <Input
              data-testid="oab-number-input"
              placeholder="Ex: 123456"
              value={numero}
              onChange={(e) => setNumero(e.target.value.replace(/\D/g, ""))}
            />
          </div>
          <div className="w-28">
            <Label className="text-xs text-stone-500 mb-1.5 block">UF</Label>
            <Select value={uf} onValueChange={setUf}>
              <SelectTrigger data-testid="oab-uf-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid="add-oab-button"
            onClick={addOab}
            className="bg-stone-900 hover:bg-stone-800 text-white gap-2"
            disabled={!numero}
          >
            <Plus className="w-4 h-4" strokeWidth={2} /> Adicionar
          </Button>
        </div>

        {/* Lote 59 — busca por nome, opcional, logo abaixo de "Número da OAB" (pedido
            explícito do Robson). Diferente da busca por OAB/processo, uma publicação
            encontrada por nome pode ser de um advogado homônimo — por isso nunca cria
            audiência sozinha, só aparece em "Publicações" marcada pra revisão manual. */}
        <div className="mb-6 max-w-sm">
          <Label className="text-xs text-stone-500 mb-1.5 block">Busca por nome (opcional)</Label>
          <Input
            data-testid="oab-nome-input"
            placeholder="Ex: Ticio da Silva"
            value={nomeAdvogado}
            onChange={(e) => setNomeAdvogado(e.target.value)}
          />
          <Dica className="text-xs text-stone-400 mt-1.5 leading-relaxed">{`Reforça a busca do DJEN pelo nome, além da OAB. Como pode haver advogados com nome parecido, publicações encontradas assim nunca viram audiência automática — só aparecem em "Publicações" para você confirmar.`}</Dica>
        </div>

          </>
        )}

        <div className="space-y-2">
          {oabs.length === 0 && <p className="text-xs text-stone-500">Nenhuma OAB cadastrada.</p>}
          {oabs.map((o) => (
            <div
              key={o.oab_id}
              data-testid={`oab-item-${o.oab_id}`}
              className="px-4 py-3 border border-stone-200 rounded-md bg-stone-50 space-y-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-stone-900 tabular-nums">
                  OAB {o.numero} / {o.uf}
                </span>
                {oabTravada ? (
                  <Button
                    data-testid={`solicitar-remocao-oab-${o.oab_id}`}
                    variant="ghost" size="sm"
                    className="text-xs text-stone-500 hover:text-stone-900"
                    onClick={() => setRemoverOab(o)}
                  >
                    Solicitar remoção
                  </Button>
                ) : (
                <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid={`delete-oab-${o.oab_id}`}
                    variant="ghost" size="sm"
                    className="text-rose-brand hover:bg-rose-100 hover:text-rose-700"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remover OAB {o.numero}/{o.uf}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      O sistema para de consultar o DJEN por essa OAB — novas publicações
                      dela não serão mais detectadas automaticamente até você cadastrar de
                      novo. Isso não afeta audiências ou clientes já cadastrados.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid={`confirm-delete-oab-${o.oab_id}`}
                      onClick={() => delOab(o.oab_id)}
                      className="bg-rose-brand hover:bg-rose-700"
                    >
                      Remover
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
                </AlertDialog>
                )}
              </div>

              {/* Lote 59 — busca por nome: exibição/edição inline, pra não ter que
                  remover e recadastrar a OAB (perderia o histórico de diagnóstico). */}
              {editingNomeOabId === o.oab_id ? (
                <div className="flex items-center gap-2">
                  <Input
                    data-testid={`oab-nome-edit-${o.oab_id}`}
                    autoFocus
                    placeholder="Nome completo do advogado"
                    value={editingNomeValue}
                    onChange={(e) => setEditingNomeValue(e.target.value)}
                    className="h-8 text-sm max-w-xs bg-white"
                  />
                  <Button
                    data-testid={`oab-nome-save-${o.oab_id}`}
                    size="sm"
                    variant="ghost"
                    disabled={savingNome}
                    onClick={() => saveNome(o.oab_id)}
                    className="h-8 px-2 text-emerald-brand hover:bg-emerald-50"
                  >
                    <Check className="w-4 h-4" strokeWidth={2} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={savingNome}
                    onClick={cancelEditNome}
                    className="h-8 px-2 text-stone-400 hover:bg-stone-100"
                  >
                    <X className="w-4 h-4" strokeWidth={2} />
                  </Button>
                </div>
              ) : o.nome_advogado ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-xs text-stone-500">
                    <UserSearch className="w-3.5 h-3.5" strokeWidth={1.75} />
                    Busca por nome: <span className="font-medium text-stone-700">{o.nome_advogado}</span>
                  </span>
                  <Button
                    data-testid={`oab-nome-edit-btn-${o.oab_id}`}
                    size="sm"
                    variant="ghost"
                    onClick={() => startEditNome(o)}
                    className="h-7 px-2 text-stone-400 hover:text-stone-700"
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid={`oab-nome-add-btn-${o.oab_id}`}
                  onClick={() => startEditNome(o)}
                  className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-emerald-brand"
                >
                  <UserSearch className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Adicionar busca por nome (opcional)
                </button>
              )}
            </div>
          ))}
        </div>
      </SecaoConfig>

      <SecaoConfig id="agenda" testid="cfg-agenda" titulo="Ver minha agenda no Google, Outlook ou Apple" icon={CalendarPlus} resumo={resumos.agenda} abrirInicial={searchParams.get("guia") === "assinatura"}>
        <SectionIntro
          summary="Assine este link uma vez no Google Agenda, Outlook ou Apple Calendar e sua agenda do IApauta aparece lá também."
          more={'Cadastre este link uma única vez no Google Agenda (De um URL), no Outlook (Adicionar calendário → Assinar da Web) ou no Apple Calendar (Arquivo → Nova assinatura de calendário) e a sua agenda do IApauta passa a aparecer lá, se atualizando sozinha de tempos em tempos — o intervalo é decidido pelo Google/Outlook/Apple, não por nós, então não é instantâneo como a sincronização direta com o Google Agenda. Toque em “Passo a passo” para ver o caminho de cada um. É só de leitura (edições feitas lá não voltam pro IApauta). Não compartilhe este link — quem tiver acesso a ele vê sua agenda; se vazar, gere um novo abaixo.'}
        />
        <div className="flex flex-wrap items-center gap-2 max-w-2xl">
          <Input
            data-testid="ics-feed-url"
            readOnly
            value={icsFeedUrl || "Gerando link…"}
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-[240px] font-mono text-xs"
          />
          <Button
            data-testid="copy-ics-feed-button"
            type="button"
            variant="outline"
            size="sm"
            onClick={copyIcsFeed}
            disabled={!icsFeedUrl}
            className="gap-2"
          >
            <Copy className="w-3.5 h-3.5" strokeWidth={1.75} /> Copiar link
          </Button>
          <Button data-testid="baixar-copia-ics-button" type="button" variant="outline" size="sm" onClick={baixarCopiaIcs} disabled={baixandoIcs} className="gap-2">
            <Download className="w-3.5 h-3.5" strokeWidth={1.75} /> {baixandoIcs ? "Baixando…" : "Baixar cópia (.ics)"}
          </Button>
          <Button data-testid="guia-assinatura-button" type="button" variant="outline" size="sm" onClick={() => setGuiaOpen(true)} className="gap-2">
            Passo a passo
          </Button>
          <AlertDialog open={confirmRotate} onOpenChange={setConfirmRotate}>
            <AlertDialogTrigger asChild>
              <Button
                data-testid="rotate-ics-feed-button"
                type="button"
                variant="outline"
                size="sm"
                className="gap-2 text-stone-500"
              >
                <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.75} /> Gerar novo link
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Gerar um novo link de assinatura?</AlertDialogTitle>
                <AlertDialogDescription>
                  O link atual para de funcionar. Quem já assinou o calendário com ele
                  (inclusive você, em outro aparelho) precisa assinar de novo com o link
                  novo.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={rotateIcsFeed} disabled={rotatingFeed}>
                  {rotatingFeed ? "Gerando…" : "Gerar novo link"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <div className="mt-4 max-w-2xl" data-testid="ics-conteudo">
          <Recolhivel
            testid="ics-conteudo-recolhivel"
            titulo="O que aparece em cada evento"
            resumo="título com tipo, partes e local abreviado; escolha o resto"
          >
            <p className="text-[11px] text-stone-500">
              O título sempre traz o tipo (audiência inicial, de instrução…), as partes e o local abreviado (ex.: “2ª VT Rib. Preto”). Marque o que mais deve ir no corpo do evento. Em segredo de justiça as partes e a descrição nunca vão.
            </p>
            {[
              ["ics_inc_link", "Link da sala", "Também fica clicável no campo “URL” do evento"],
              ["ics_inc_sala", "ID e senha da sala", ""],
              ["ics_inc_partes", "Partes (autor x réu)", ""],
              ["ics_inc_local", "Vara e comarca", ""],
              ["ics_inc_fase", "Fase da audiência e tipo do prazo", ""],
              ["ics_inc_descricao", "Descrição", ""],
              ["ics_inc_endereco", "Endereço do compromisso presencial", ""],
              ["ics_inc_cliente", "Cliente vinculado", "Desligado por padrão: o calendário da Apple é lido pelos servidores da Apple"],
              ["ics_inc_abrir", "Link “Abrir no IApauta”", ""],
            ].map(([k, rot, dica]) => (
              <label key={k} className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  data-testid={`${k.replace(/_/g, "-")}-checkbox`}
                  checked={!!settings[k]}
                  onCheckedChange={(v) => setSettings({ ...settings, [k]: Boolean(v) })}
                  className="mt-0.5"
                />
                <span className="text-sm text-stone-700 flex-1">
                  <span className="font-medium text-stone-900">{rot}</span>
                  {dica ? <span className="text-[11px] text-stone-500 block mt-0.5">{dica}</span> : null}
                </span>
              </label>
            ))}
            <Button data-testid="save-ics-conteudo-button" onClick={saveSettings} variant="outline" className="text-stone-700">
              Salvar o que aparece no calendário
            </Button>
            <p className="text-[11px] text-stone-500">O mesmo link continua valendo. A Apple relê o calendário sozinha de tempos em tempos.</p>
          </Recolhivel>
        </div>
      </SecaoConfig>
      <AssinarAgendaGuia open={guiaOpen} onOpenChange={fecharGuia} feedUrl={icsFeedUrl} onCopy={copyIcsFeed} />

      <SecaoConfig id="prazos" testid="cfg-prazos" titulo="Prazos" icon={Clock3} resumo={resumos.prazos}>
        <SectionIntro
          summary="Controla quando o app cria pendências de prazo automaticamente."
          more="Quando o app cria pendências/eventos de prazo sozinho, sem você pedir. Os dois abaixo não mudam nada além disso — a sincronização com o DJEN continua igual."
        />
        <div className="space-y-4 max-w-md">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="auto-prazo-audiencias-checkbox"
              checked={settings.auto_prazo_audiencias}
              onCheckedChange={(v) => setSettings({ ...settings, auto_prazo_audiencias: Boolean(v) })}
              className="mt-0.5"
            />
            <span className="text-sm text-stone-700">
              <span className="font-medium text-stone-900">Prazo automático para audiências</span> — toda
              audiência aprovada já agenda sozinha a pendência "conferir a ata" 5 dias úteis
              depois (configurável acima). Padrão: ligado.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="auto-prazo-publicacoes-checkbox"
              checked={settings.auto_prazo_publicacoes}
              onCheckedChange={(v) => setSettings({ ...settings, auto_prazo_publicacoes: Boolean(v) })}
              className="mt-0.5"
            />
            <span className="text-sm text-stone-700">
              <span className="font-medium text-stone-900">Prazo automático para todas as publicações</span> —
              rede de segurança: mesmo quando a IA não identifica audiência/prazo numa
              publicação, cria assim mesmo um prazo de 5 dias úteis em "Para validar", pra
              você conferir e editar ou descartar. Evita duplicar publicações repetidas do
              mesmo processo. Pode gerar bastante item na agenda — ainda em teste.
              Padrão: desligado.
            </span>
          </label>

          <Button
            data-testid="save-prazos-settings-button"
            onClick={saveSettings}
            variant="outline"
            className="text-stone-700"
          >
            Salvar preferências de prazo
          </Button>
        </div>
      </SecaoConfig>

      <SecaoConfig id="privacidade" testid="cfg-privacidade" titulo="Privacidade na IA" icon={ShieldCheck} resumo={resumos.privacidade}>
        <SectionIntro
          summary="Esconde nomes e dados pessoais do texto antes de ele ir para a IA."
          more="Antes de a IA ler uma intimação, o app troca os nomes das partes, advogados, peritos e testemunhas por rótulos (Parte 1, Perito 1…), e também CPF/CNPJ, telefones, e-mails, endereços e o número do processo. A resposta da IA volta com os nomes reais, restaurados aqui no app. É uma proteção por heurística: reduz bastante a exposição, mas não é garantia absoluta de que nenhum nome passe."
        />
        <div className="space-y-4 max-w-md">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="ai-mask-names-checkbox"
              checked={settings.ai_mask_names}
              onCheckedChange={(v) => setSettings({ ...settings, ai_mask_names: Boolean(v) })}
              className="mt-0.5"
            />
            <span className="text-sm text-stone-700">
              <span className="font-medium text-stone-900">Proteger nomes ao usar a IA</span> — a IA
              recebe o texto da intimação sem os nomes das pessoas. Padrão: ligado.
            </span>
          </label>
          <p className="text-xs text-stone-500 leading-relaxed" data-testid="secret-always-note">
            Processos em <strong>segredo de justiça</strong> são sempre protegidos, mesmo com esta
            opção desligada: nenhum nome vai para a IA, nenhum nome de parte nem o texto da
            intimação é guardado no app ou no banco. Você pode marcar um processo como sigiloso
            na caixa de Publicações.
          </p>
          <Button
            data-testid="save-privacy-settings-button"
            onClick={saveSettings}
            variant="outline"
            className="text-stone-700"
          >
            Salvar privacidade
          </Button>
        </div>
      </SecaoConfig>

      <SecaoConfig id="assistente" testid="cfg-assistente" titulo="Assistente com IA" icon={Sparkles} resumo={resumos.assistente}>
        <SectionIntro
          summary="Perguntas livres sobre a sua agenda e resumo das movimentações de um processo. Só consulta: não altera nem envia nada."
          more="Os exemplos do Assistente são respondidos pelo próprio app, sem IA e sem limite. Perguntas livres e o resumo de processo usam a IA (modelo econômico), até 40 por dia por conta; o limite volta no dia seguinte. Antes de sair do servidor, nomes, CPF/CNPJ, telefones, e-mails e números de processo são trocados por códigos, e a resposta volta com os nomes reais. Processos em segredo de justiça nunca são enviados. Cada resposta mostra as fontes (links para os registros usados) e deve ser conferida antes de agir. O texto das suas perguntas não fica gravado."
        />
        <div className="space-y-4 max-w-md">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="ia-assistente-checkbox"
              checked={settings.ia_assistente_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, ia_assistente_enabled: Boolean(v) })}
              className="mt-0.5"
            />
            <span className="text-sm text-stone-700">
              <span className="font-medium text-stone-900">Usar IA no Assistente</span> — perguntas livres e resumo de processo. Desmarcado, só os exemplos (sem IA) funcionam. Padrão: ligado.
            </span>
          </label>
          <Button data-testid="save-ia-settings-button" onClick={saveSettings} variant="outline" className="text-stone-700">
            Salvar
          </Button>
        </div>
      </SecaoConfig>

      <SecaoConfig id="tribunais" testid="cfg-tribunais" titulo="Tribunais do caderno diário" icon={Landmark} resumo={resumos.tribunais}>
        <SectionIntro
          summary="Marque os tribunais onde você mais atua. O app lê o diário completo deles todo dia, junto com a busca do DJEN."
          more="Além da busca por OAB, o app baixa o caderno (diário completo) de cada tribunal marcado e procura as suas OABs dentro dele. Isso pega publicações que a busca do DJEN ainda não indexou. Cada caderno tem em média 10 a 15 MB, é baixado direto do CNJ, lido e apagado em seguida: nada do caderno fica guardado, só as publicações que são suas. Roda às 6h e às 16h (logo depois da busca do DJEN) e quando você clica em Sincronizar. Sem nenhuma marcação, o app usa os tribunais da UF das suas OABs (em SP: TJSP, TRT15 e TRT2). Cada tribunal marcado aumenta o tempo e o consumo da leitura, então marque só os que você usa de fato."
        />
        <div className="space-y-5 max-w-3xl">
          {CADERNO_GRUPOS.map((g) => (
            <div key={g.titulo}>
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">{g.titulo}</p>
              <div className="flex flex-wrap gap-1.5">
                {g.itens.map((sigla) => {
                  const on = (settings.caderno_tribunais || []).includes(sigla);
                  return (
                    <button
                      key={sigla}
                      type="button"
                      data-testid={`caderno-tribunal-${sigla}`}
                      aria-pressed={on}
                      onClick={() => toggleTribunal(sigla)}
                      className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
                        on
                          ? SEL_CINZA
                          : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                      }`}
                    >
                      {sigla}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="caderno-auto-checkbox"
              checked={settings.caderno_auto_tribunais}
              onCheckedChange={(v) => setSettings({ ...settings, caderno_auto_tribunais: Boolean(v) })}
              className="mt-0.5"
            />
            <span className="text-sm text-stone-700">
              Incluir também, automaticamente, os tribunais em que já recebi publicações ou onde
              tenho processos cadastrados.
            </span>
          </label>
          <p className="text-xs text-stone-500" data-testid="caderno-count-note">
            {(settings.caderno_tribunais || []).length === 0
              ? "Nenhum tribunal marcado: o app usa o padrão pela UF das suas OABs."
              : `${settings.caderno_tribunais.length} tribunal(is) marcado(s): ${settings.caderno_tribunais.join(", ")}.`}
          </p>
          <Button
            data-testid="save-caderno-settings-button"
            onClick={saveSettings}
            variant="outline"
            className="text-stone-700"
          >
            Salvar tribunais
          </Button>
        </div>
      </SecaoConfig>

      <SecaoConfig id="palavras" testid="cfg-palavras" titulo="Palavras-chave em Publicações" icon={Tag} resumo={resumos.palavras}>
        <SectionIntro
          summary="Destaca publicações que contenham essas palavras na caixa de Publicações."
          more={'Publicações cujo texto contenha alguma dessas palavras ganham um destaque visual na caixa de Publicações, pra chamar sua atenção num relance (ex: "contestação", "réplica", "recurso"). É só um destaque na tela — não aciona IA nem cria nada sozinho.'}
        />
        <div className="flex flex-wrap items-end gap-3 mb-4 max-w-md">
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs text-stone-500 mb-1.5 block">Nova palavra-chave</Label>
            <Input
              data-testid="keyword-input"
              placeholder="Ex: contestação"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
            />
          </div>
          <Button
            data-testid="add-keyword-button"
            type="button"
            onClick={addKeyword}
            disabled={!keywordInput.trim()}
            className="bg-stone-900 hover:bg-stone-800 text-white gap-2"
          >
            <Plus className="w-4 h-4" strokeWidth={2} /> Adicionar
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(settings.keywords || []).length === 0 && (
            <p className="text-xs text-stone-500">Nenhuma palavra-chave cadastrada.</p>
          )}
          {(settings.keywords || []).map((kw) => (
            <span
              key={kw}
              data-testid={`keyword-chip-${kw}`}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-amber-100 border border-amber-200 text-amber-700 text-xs font-medium"
            >
              {kw}
              <button
                type="button"
                data-testid={`remove-keyword-${kw}`}
                onClick={() => removeKeyword(kw)}
                className="hover:bg-amber-100 rounded-full p-0.5"
              >
                <X className="w-3 h-3" strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </div>

        <Button
          data-testid="save-keywords-settings-button"
          onClick={saveSettings}
          variant="outline"
          className="text-stone-700 mt-4"
        >
          Salvar palavras-chave
        </Button>
      </SecaoConfig>

      <SecaoConfig id="identidade" testid="cfg-identidade" titulo="Identidade nos avisos ao cliente" icon={BadgeCheck} resumo={resumos.identidade}>
        <IdentidadeCard />
      </SecaoConfig>

      <SecaoConfig id="lembretes" testid="cfg-lembretes" titulo="Lembretes automáticos" icon={Bell} resumo={resumos.lembretes}>
        <SectionIntro
          summary="Preferências de aviso aos clientes antes de cada audiência."
          more="Define quantos dias antes avisamos o cliente e por quais canais (e-mail, WhatsApp), além de quantos dias úteis depois da audiência te lembramos de conferir a ata."
        />

        <div className="space-y-6 max-w-md">
          <div>
            <Label className="text-xs text-stone-500 mb-1.5 block">Antecedência (dias antes da audiência)</Label>
            <Input
              data-testid="days-before-input"
              type="number" min="1" max="30"
              value={settings.days_before}
              onChange={(e) => setSettings({ ...settings, days_before: parseInt(e.target.value || "3") })}
            />
            <p className="text-[11px] text-stone-500 mt-1">
              Quando disparamos o aviso ao cliente antes de cada audiência.
            </p>
          </div>

          <div>
            <Label className="text-xs text-stone-500 mb-1.5 block">Conferir prazos da ata (dias úteis depois)</Label>
            <Input
              data-testid="post-hearing-days-input"
              type="number" min="1" max="60"
              value={settings.post_hearing_days}
              onChange={(e) => setSettings({ ...settings, post_hearing_days: parseInt(e.target.value || "5") })}
            />
            <p className="text-[11px] text-stone-500 mt-1">
              Toda audiência já agenda automaticamente, na aba "Prazos", uma pendência para
              você conferir a ata esse tanto de <strong>dias úteis</strong> depois — e te
              avisamos por e-mail nessa data, caso ainda não tenha resolvido. Padrão: 5 dias úteis.
            </p>
          </div>

          <div>
            <Label className="text-xs text-stone-500 mb-2 block">Canais habilitados</Label>
            <div className="space-y-2">
              {["email", "whatsapp"].map((ch) => (
                <label key={ch} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    data-testid={`channel-${ch}`}
                    checked={(settings.channels || []).includes(ch)}
                    onCheckedChange={() => toggleChannel(ch)}
                  />
                  <span className="text-sm text-stone-700 capitalize">{ch}</span>
                </label>
              ))}
            </div>
          </div>

          <Button
            data-testid="save-settings-button"
            onClick={saveSettings}
            className="bg-emerald-brand text-white"
          >
            Salvar preferências
          </Button>
        </div>
      </SecaoConfig>

      <SecaoConfig id="emailself" testid="cfg-emailself" titulo="Aviso de audiência por e-mail" icon={Mail} resumo={resumos.emailself}>
        <SectionIntro
          summary="Um e-mail pra você mesmo antes de cada audiência — diferente do lembrete ao cliente acima."
          more="Chega na sua caixa de entrada (não é notificação no navegador, como a seção de baixo) com os dados da audiência e o link pra abrir no app. Independe de a audiência ter cliente vinculado."
        />

        <div className="space-y-6 max-w-md">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="email-self-enabled-checkbox"
              checked={settings.email_self_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, email_self_enabled: Boolean(v) })}
              className="mt-0.5"
            />
            <span className="text-sm text-stone-700 flex-1">
              <span className="font-medium text-stone-900">Enviar aviso de audiência por e-mail para mim</span>
              <Input
                data-testid="email-self-days-before-input"
                type="number" min="1" max="30"
                value={settings.email_self_days_before}
                onChange={(e) => setSettings({ ...settings, email_self_days_before: parseInt(e.target.value || "1") })}
                className="mt-2 w-24 h-8 text-xs"
                onClick={(e) => e.preventDefault()}
              />
              <span className="text-[11px] text-stone-500 block mt-1">Dias de antecedência (padrão: 1)</span>
            </span>
          </label>

          <Button
            data-testid="save-email-self-settings-button"
            onClick={saveSettings}
            variant="outline"
            className="text-stone-700"
          >
            Salvar preferência de aviso por e-mail
          </Button>
        </div>
      </SecaoConfig>

      <SecaoConfig id="digest" testid="digest-settings" titulo="Resumo por e-mail" icon={Send} resumo={resumos.digest}>
        <SectionIntro
          summary="Vem ligado por padrão: um e-mail depois de cada busca (6h30 e 16h30), com a agenda e as publicações novas. Desmarque abaixo para parar."
          more="De manhã: os compromissos de hoje, os prazos vencidos ainda pendentes e as publicações que chegaram desde o último resumo. À tarde: o que ainda falta hoje, os compromissos de amanhã e as publicações novas. Em processo em segredo de justiça o e-mail não mostra nomes nem texto. A manhã sai todo dia útil; a tarde só sai quando há novidade (a menos que você marque a última opção). Chega no e-mail da sua conta. Toda segunda-feira o resumo da manhã leva em anexo uma cópia do seu calendário (.ics). Em cada publicação, as partes, a vara/comarca e o número do processo vêm em destaque."
        />

        <div className="space-y-5 max-w-md">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="digest-manha-checkbox"
              checked={settings.digest_manha_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, digest_manha_enabled: Boolean(v) })}
              className="mt-0.5"
            />
            <span className="text-sm text-stone-700 flex-1">
              <span className="font-medium text-stone-900">Resumo das 6h30</span>
              <span className="text-[11px] text-stone-500 block mt-0.5">Agenda de hoje, prazos vencidos e publicações novas</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="digest-tarde-checkbox"
              checked={settings.digest_tarde_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, digest_tarde_enabled: Boolean(v) })}
              className="mt-0.5"
            />
            <span className="text-sm text-stone-700 flex-1">
              <span className="font-medium text-stone-900">Resumo das 16h30</span>
              <span className="text-[11px] text-stone-500 block mt-0.5">O que falta hoje, compromissos de amanhã e publicações novas</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="digest-sempre-checkbox"
              checked={settings.digest_sempre}
              onCheckedChange={(v) => setSettings({ ...settings, digest_sempre: Boolean(v) })}
              className="mt-0.5"
            />
            <span className="text-sm text-stone-700 flex-1">
              <span className="font-medium text-stone-900">Enviar mesmo sem novidades</span>
              <span className="text-[11px] text-stone-500 block mt-0.5">Sem isso, a tarde e os fins de semana só mandam e-mail quando há algo a dizer</span>
            </span>
          </label>

          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="save-digest-settings-button"
              onClick={saveSettings}
              variant="outline"
              className="text-stone-700"
            >
              Salvar resumo por e-mail
            </Button>
            <Button
              data-testid="digest-teste-button"
              onClick={enviarExemploDigest}
              disabled={enviandoDigest}
              variant="ghost"
              className="text-emerald-brand"
            >
              {enviandoDigest ? "Enviando..." : "Enviar exemplo agora"}
            </Button>
          </div>
        </div>
      </SecaoConfig>

      <SecaoConfig id="prio" testid="prio-settings" titulo="Processos prioritários" icon={Star} resumo={resumos.prio}>
        <SectionIntro
          summary="Como o app avisa quando um processo marcado com ★ tem movimentação nova."
          more="O aviso sai logo depois de cada busca (6h e 16h), um só com o resumo — nunca um por item. Em processo em segredo de justiça o aviso vai sem nomes e sem texto. O push só chega se as notificações estiverem ativadas neste aparelho (seção abaixo)."
        />
        <div className="space-y-5 max-w-md">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox data-testid="prio-push-checkbox" checked={settings.prio_push_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, prio_push_enabled: Boolean(v) })} className="mt-0.5" />
            <span className="text-sm text-stone-700 font-medium text-stone-900">Avisar por push</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox data-testid="prio-email-checkbox" checked={settings.prio_email_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, prio_email_enabled: Boolean(v) })} className="mt-0.5" />
            <span className="text-sm text-stone-700 font-medium text-stone-900">Avisar por e-mail (um por busca)</span>
          </label>
          <label className="block text-sm text-stone-700">
            <span className="font-medium text-stone-900 block mb-1">Revisão periódica padrão</span>
            <select data-testid="prio-revisao-select" value={settings.prio_revisao_padrao}
              onChange={(e) => setSettings({ ...settings, prio_revisao_padrao: e.target.value })}
              className="h-10 px-3 rounded-md border border-stone-300 bg-white text-sm">
              <option value="semanal">Semanal</option>
              <option value="quinzenal">Quinzenal</option>
              <option value="mensal">Mensal</option>
              <option value="nenhuma">Sem revisão</option>
            </select>
            <span className="text-[11px] text-stone-500 block mt-1">Cada processo pode ter a sua, no bloco Acompanhamento.</span>
          </label>
          <Button data-testid="save-prio-settings-button" onClick={saveSettings} variant="outline" className="text-stone-700">
            Salvar preferências dos prioritários
          </Button>
        </div>
      </SecaoConfig>

      <SecaoConfig id="push" testid="cfg-push" titulo="Notificações push" icon={BellRing} resumo={resumos.push}>
        <SectionIntro
          summary="Avisos na tela do seu desktop ou celular — só para você e sua equipe."
          more="Mesmo com o site fechado, desde que o navegador esteja aberto. Não é enviado ao cliente. Precisa ser ativado em cada navegador/aparelho separadamente."
        />

        <div className="space-y-6 max-w-md">
          {!isPushSupported() ? (
            <p className="text-xs text-stone-500">Este navegador não tem suporte a notificações push.</p>
          ) : (
            <Button
              data-testid="toggle-push-button"
              type="button"
              variant={pushSubscribed ? "outline" : "default"}
              onClick={togglePush}
              disabled={pushBusy}
              className={pushSubscribed ? "gap-2 text-stone-500" : "gap-2 bg-emerald-brand text-white"}
            >
              <Bell className="w-4 h-4" strokeWidth={1.75} />
              {pushBusy
                ? "Aguarde…"
                : pushSubscribed
                  ? "Notificações ativadas neste navegador — desativar"
                  : "Ativar notificações neste navegador"}
            </Button>
          )}
          {isPushSupported() && getPushPermission() === "denied" && (
            <p className="text-xs text-amber-700 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={2} />
              As notificações estão bloqueadas nas configurações do navegador — permita o
              site para poder ativar.
            </p>
          )}

          <div className="space-y-4 pt-2 border-t border-stone-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                data-testid="push-day-before-checkbox"
                checked={settings.push_day_before_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, push_day_before_enabled: Boolean(v) })}
                className="mt-0.5"
              />
              <span className="text-sm text-stone-700 flex-1">
                <span className="font-medium text-stone-900">Lembrete 1 dia antes</span>
                <Input
                  data-testid="push-day-before-hours-input"
                  type="number" min="1" max="72"
                  value={settings.push_day_before_hours}
                  onChange={(e) => setSettings({ ...settings, push_day_before_hours: parseInt(e.target.value || "24") })}
                  className="mt-2 w-24 h-8 text-xs"
                  onClick={(e) => e.preventDefault()}
                />
                <span className="text-[11px] text-stone-500 block mt-1">Horas de antecedência (padrão: 24)</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                data-testid="push-hour-before-checkbox"
                checked={settings.push_hour_before_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, push_hour_before_enabled: Boolean(v) })}
                className="mt-0.5"
              />
              <span className="text-sm text-stone-700 flex-1">
                <span className="font-medium text-stone-900">Lembrete pouco antes</span>
                <Input
                  data-testid="push-hour-before-hours-input"
                  type="number" min="1" max="24"
                  value={settings.push_hour_before_hours}
                  onChange={(e) => setSettings({ ...settings, push_hour_before_hours: parseInt(e.target.value || "1") })}
                  className="mt-2 w-24 h-8 text-xs"
                  onClick={(e) => e.preventDefault()}
                />
                <span className="text-[11px] text-stone-500 block mt-1">Horas de antecedência (padrão: 1)</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                data-testid="push-conflict-checkbox"
                checked={settings.push_conflict_alerts_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, push_conflict_alerts_enabled: Boolean(v) })}
                className="mt-0.5"
              />
              <span className="text-sm text-stone-700">
                <span className="font-medium text-stone-900">Avisar sobre conflito de horário</span> — quando
                dois compromissos aprovados caem no mesmo dia com menos de 1h de diferença.
                Não impede o agendamento, só avisa.
              </span>
            </label>
          </div>

          <Button
            data-testid="save-push-settings-button"
            onClick={saveSettings}
            variant="outline"
            className="text-stone-700"
          >
            Salvar preferências de notificação
          </Button>
        </div>
      </SecaoConfig>

      <SecaoConfig id="ligacao" testid="cfg-ligacao" titulo="Aviso por ligação telefônica" icon={Phone} resumo={resumos.ligacao}>
        <SectionIntro
          summary="Uma ligação automática pra você — de manhã no dia do compromisso e pouco antes dele — pra quando um push ou e-mail pode passar despercebido."
          more="Liga pro número que você cadastrar abaixo, com uma mensagem de voz curta dizendo o compromisso e o horário. Não depende do navegador aberto nem de internet no celular no momento da ligação. Cada ligação tem um custo por minuto (fornecedor Zenvia) — use com moderação."
        />

        <div className="space-y-6 max-w-md">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="call-enabled-checkbox"
              checked={settings.call_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, call_enabled: Boolean(v) })}
              className="mt-0.5"
            />
            <span className="text-sm text-stone-700 flex-1">
              <span className="font-medium text-stone-900">Ativar lembrete por ligação</span>
              <Input
                data-testid="call-phone-input"
                type="tel"
                placeholder="(11) 99999-8888"
                value={settings.call_phone}
                onChange={(e) => setSettings({ ...settings, call_phone: e.target.value })}
                className="mt-2 w-48 h-8 text-xs"
                onClick={(e) => e.preventDefault()}
              />
              <span className="text-[11px] text-stone-500 block mt-1">Número que vai receber a ligação (com DDD)</span>
            </span>
          </label>

          <div className="space-y-4 pt-2 border-t border-stone-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                data-testid="call-day-before-checkbox"
                checked={settings.call_day_before_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, call_day_before_enabled: Boolean(v) })}
                className="mt-0.5"
              />
              <span className="text-sm text-stone-700 flex-1">
                <span className="font-medium text-stone-900">Ligar dias antes</span>
                <Input
                  data-testid="call-day-before-days-input"
                  type="number" min="1" max="30"
                  value={settings.call_day_before_days}
                  onChange={(e) => setSettings({ ...settings, call_day_before_days: parseInt(e.target.value || "1") })}
                  className="mt-2 w-24 h-8 text-xs"
                  onClick={(e) => e.preventDefault()}
                />
                <span className="text-[11px] text-stone-500 block mt-1">Dias de antecedência (padrão: 1 dia)</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                data-testid="call-morning-checkbox"
                checked={settings.call_morning_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, call_morning_enabled: Boolean(v) })}
                className="mt-0.5"
              />
              <span className="text-sm text-stone-700 flex-1">
                <span className="font-medium text-stone-900">Ligar de manhã, no dia do compromisso</span>
                <Input
                  data-testid="call-morning-hour-input"
                  type="number" min="5" max="11"
                  value={settings.call_morning_hour}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value || "7");
                    setSettings({ ...settings, call_morning_hour: Math.min(11, Math.max(5, Number.isNaN(raw) ? 7 : raw)) });
                  }}
                  className="mt-2 w-24 h-8 text-xs"
                  onClick={(e) => e.preventDefault()}
                />
                <span className="text-[11px] text-stone-500 block mt-1">Hora da ligação, de 5 a 11 h (padrão: 7 h). Uma só ligação por dia, listando os compromissos de hoje.</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                data-testid="call-minutes-before-checkbox"
                checked={settings.call_minutes_before_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, call_minutes_before_enabled: Boolean(v) })}
                className="mt-0.5"
              />
              <span className="text-sm text-stone-700 flex-1">
                <span className="font-medium text-stone-900">Ligar pouco antes</span>
                <Input
                  data-testid="call-minutes-before-input"
                  type="number" min="5" max="180"
                  value={settings.call_minutes_before}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value || "60");
                    const clamped = Math.min(180, Math.max(5, Number.isNaN(raw) ? 60 : raw));
                    setSettings({ ...settings, call_minutes_before: clamped });
                  }}
                  className="mt-2 w-24 h-8 text-xs"
                  onClick={(e) => e.preventDefault()}
                />
                <span className="text-[11px] text-stone-500 block mt-1">Minutos de antecedência, de 5 a 180 (padrão: 60 = 1 hora)</span>
              </span>
            </label>
          </div>

          <div className="space-y-2 pt-2 border-t border-stone-200">
            <Label className="text-xs text-stone-500 block">Ligar apenas para estes tipos de compromisso</Label>
            <div className="space-y-2">
              {[
                { value: "audiencia", label: "Audiências" },
                { value: "prazo", label: "Prazos" },
                { value: "reuniao", label: "Compromissos" },
              ].map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    data-testid={`call-event-type-${value}`}
                    checked={(settings.call_event_types || []).includes(value)}
                    onCheckedChange={() => toggleCallEventType(value)}
                  />
                  <span className="text-sm text-stone-700">{label}</span>
                </label>
              ))}
            </div>
            <Dica className="text-[11px] text-stone-500">{`Vale só pro lembrete padrão acima — um compromisso com "Ativar"/"Desativar" forçado no próprio agendamento continua seguindo essa escolha específica, independente desse filtro.`}</Dica>
          </div>

          <Button
            data-testid="save-call-settings-button"
            onClick={saveSettings}
            variant="outline"
            className="text-stone-700"
          >
            Salvar preferências de ligação
          </Button>
        </div>
      </SecaoConfig>
    </div>
  );
}
